/**
 * write.js — knowing whether a write actually landed.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE PROBLEM
 *
 * Firestore's offline persistence makes `await setDoc(...)` mean something
 * different from what it looks like. The local cache applies the write
 * immediately, so the UI updates at once — but the promise does not resolve
 * until the SERVER acknowledges it. Three outcomes, and the code usually
 * handles one:
 *
 *   resolves   the server took it. Genuinely saved.
 *   rejects    the server refused it — a rules denial. Handled by catch.
 *   NEITHER    the write is queued and cannot reach the server. The promise
 *              stays pending indefinitely. There is no error, no timeout, and
 *              no callback. It simply never finishes.
 *
 * That third case is what made voting on a poll sit on a spinner forever, and
 * what let a saved paper look saved and be gone on the next device. `finally`
 * does not help: the promise never settles, so `finally` never runs.
 *
 * WHAT THIS DOES
 *
 * Races the write against a deadline and reports which happened, so callers
 * can stop blocking the interface without lying about the outcome. A queued
 * write is not an error — Firestore really will send it when the connection
 * returns — but it is not "saved" either, and the difference matters when
 * someone closes the tab.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** How long to wait for a server acknowledgement before saying so. */
const ACK_MS = 6000;

/**
 * @param {Promise} promise  a Firestore write
 * @returns {Promise<'saved'|'queued'>}  resolves; rejects only on a real error
 */
export function confirmWrite(promise, ms = ACK_MS) {
  let timer;
  return Promise.race([
    // A rejection still propagates, so rules denials surface as before.
    promise.then(() => 'saved'),
    new Promise((resolve) => {
      timer = setTimeout(() => resolve('queued'), ms);
    }),
  ]).finally(() => clearTimeout(timer));
}

/**
 * The sentence to show when a write is queued rather than stored.
 *
 * Deliberately not an error, and deliberately not silence. It tells the truth —
 * the change is on this device and will sync — without implying something has
 * gone wrong, because usually nothing has.
 */
export const QUEUED_MESSAGE =
  'Saved on this device. It will sync when your connection comes back.';
